import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import {Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
   TableFooter, TableHead, TablePagination, TableRow, Typography} from '@mui/material';

import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import {User} from '../../../data/interfaces/usermodels';
import userService from '../../../data/api/user.service';
import { useAppSelector, useAppDispatch } from '../../../stores/hooks' 
import { Delete, Edit } from '@mui/icons-material';
import ConfirmDialog from '../../../shared/confirmdialog';
import { showsuccessmessage } from '../../../stores/notificationslice';
import UserUpdateForm from './userupdateform';
import { useCallback } from 'react';

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}



export default function UsersTable() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [users,setUsers]=React.useState<User[]>([]);
  const [isDialogOpen,setDialogOpen]=React.useState<boolean>(false);
  const [message,setMessage]=React.useState<string>("");
  const [title,setTitle]=React.useState<string>("");
  const [usertoDelete,setUsertoDelete]=React.useState<number|null>(null);
  const [needtoLoad,setNeedtoLoad]=React.useState<boolean>(true);
  const dispatch=useAppDispatch();

  React.useEffect(()=>{
    if(needtoLoad){
    userService.getusers(false).then((response)=>{
        setUsers(response.data);
    })
    setNeedtoLoad(false);
  }
  },[needtoLoad]);
  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete=(id:number)=>{
    setUsertoDelete(id);
    setTitle("Delete Use");
    setMessage("Are you really want to delete the user?");
    setDialogOpen(true);
    
  }

  const dialogCallback=(confirmed:boolean,remarks:string)=>{
    setDialogOpen(false);
    if(confirmed){
      userService.deleteuser(usertoDelete??0).then((response)=>{
        if(response&&response.data){
          setNeedtoLoad(true);
          dispatch(showsuccessmessage("User deleted successfully."))
        }
      });
    }
    
  }

  const createCallback=useCallback(()=>{
    setNeedtoLoad(true);
  },[])

  return (
    <Box>
       <UserUpdateForm callback={createCallback}/>
    
    <TableContainer  component={Paper}sx={{  margin: '8px', padding: '16px' ,width:'calc(100% - 32px)' }}>
      <Typography variant='h6'>Users</Typography>
      <Table  sx={{ minWidth: 500}} aria-label="custom pagination table" size="small">
         <TableHead>
          <TableRow>
            <TableCell sx={{fontWeight:'bold'}}>Name</TableCell>
            <TableCell sx={{fontWeight:'bold'}}>Email</TableCell>
            <TableCell sx={{fontWeight:'bold'}}>User Name</TableCell>
            <TableCell sx={{fontWeight:'bold'}}>Company</TableCell>
            <TableCell sx={{fontWeight:'bold'}}>Role</TableCell>
            <TableCell sx={{fontWeight:'bold'}}>Edit</TableCell>
            <TableCell sx={{fontWeight:'bold'}}>Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : users
          ).map((user:User) => (
            <TableRow key={user.Id}>
              <TableCell>
                {user.FullName}
              </TableCell>
              <TableCell  >
                {user.Email}
              </TableCell>
              <TableCell  >
                {user.UserName}
              </TableCell>
              <TableCell >
                {user.Toc}
              </TableCell>
              <TableCell >
                {user.Role}
              </TableCell>
              <TableCell style={{ width: 60 }}>
                <IconButton>
                  <Edit  sx={{color:'primary.main'}}/>
                </IconButton>
              </TableCell>
              <TableCell style={{ width: 60 }}>
                <IconButton onClick={()=>handleDelete(user.Id)}>
                  <Delete sx={{color:'primary.main'}} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, { label: 'All', value: -1 }]}
              colSpan={3}
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <ConfirmDialog needComments={true} isopen={isDialogOpen} title={title} message={message} callback={dialogCallback} />
    </TableContainer>
    </Box>
  );
}